/**
 * Conversion between UTC instants (as stored, e.g. `2026-09-19T21:00:00.000Z`)
 * and the club's local wall-clock time (`Australia/Sydney`), for use in
 * `datetime-local` admin inputs.
 *
 * Why this exists: `datetime-local` inputs have no timezone concept — they
 * show and accept wall-clock time in whatever zone the BROWSER is running in.
 * The previous `formatDateForInput` used `Date.getHours()` etc., which reads
 * the browser's local zone. Stored data is correct — `2026-09-19T21:00:00Z`
 * really is 7am AEST — but an admin opening the form on a non-NSW machine
 * would see a shifted time (e.g. 5am AWST) and could "correct" an apparently
 * wrong value, silently moving the competition. See
 * `.kiro/specs/competition-timing-canonical/requirements.md` R2.
 *
 * Implemented with `Intl.DateTimeFormat` + `timeZone`, which resolves the
 * zone's UTC offset for the specific instant in question — so this is
 * DST-aware rather than assuming a fixed +10:00. AEDT begins 4 Oct 2026.
 */

import { CLUB_TIMEZONE } from '../../../shared/competitionRules';

const CLUB_TIME_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: CLUB_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const CLUB_ZONE_ABBREVIATION_FORMATTER = new Intl.DateTimeFormat('en-AU', {
  timeZone: CLUB_TIMEZONE,
  timeZoneName: 'short',
});

/**
 * Returns the club timezone's abbreviation ("AEST" or "AEDT") for the given
 * instant (default: now), for labelling admin inputs — e.g. "Fishing starts
 * (AEST)". Resolved per instant rather than hardcoded, so the label is
 * correct on both sides of the 4 Oct 2026 DST transition.
 */
export function getClubTimezoneAbbreviation(instant: Date = new Date()): string {
  const part = CLUB_ZONE_ABBREVIATION_FORMATTER.formatToParts(instant).find(
    (p) => p.type === 'timeZoneName',
  );
  return part?.value ?? 'AEST';
}

interface ClubWallClockParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
}

/**
 * Reads the club-local wall-clock date/time parts for a given UTC instant.
 */
function getClubWallClockParts(instant: Date): ClubWallClockParts {
  const parts = CLUB_TIME_FORMATTER.formatToParts(instant);
  const get = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value);

  let hour = get('hour');
  // en-CA + hour12: false renders midnight as "24", not "00"
  if (hour === 24) hour = 0;

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
  };
}

/**
 * Converts a UTC ISO instant to the `YYYY-MM-DDTHH:mm` value a
 * `datetime-local` input expects, representing that instant in club local
 * time (`Australia/Sydney`), DST-aware.
 *
 * Returns "" for a null/undefined/unparseable input, matching the previous
 * `formatDateForInput`'s fail-empty behaviour so callers don't need to change
 * their null handling.
 */
export function toClubInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const { year, month, day, hour, minute } = getClubWallClockParts(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

/**
 * Converts a `datetime-local` input value (`YYYY-MM-DDTHH:mm`), interpreted
 * as club local time (`Australia/Sydney`), to a UTC ISO instant.
 *
 * There is no native "parse as if in timezone X" in JS, so this resolves the
 * correct offset by: parsing the wall-clock value as a UTC instant (a guess),
 * checking what club-local time that guess actually renders as via
 * `getClubWallClockParts`, and correcting for the difference — then
 * re-checking, because a guess that happens to land on the far side of a DST
 * transition from the true instant will be corrected using the WRONG offset,
 * landing up to an hour off (observed for wall-clock values shortly before
 * the 4 Oct 2026 AEDT transition, where the naive one-shot guess fell after
 * it). Iterating converges immediately once guess and truth are on the same
 * side of any transition, which one correction is always enough to achieve
 * for a real IANA zone's offsets.
 *
 * Returns "" for an empty/unparseable input.
 */
export function fromClubInputValue(value: string | null | undefined): string {
  if (!value) return '';

  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return '';
  const [, y, mo, d, h, mi] = match;
  const wanted = {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hour: Number(h),
    minute: Number(mi),
  };
  const wantedAsUtc = Date.UTC(
    wanted.year,
    wanted.month - 1,
    wanted.day,
    wanted.hour,
    wanted.minute,
  );
  if (Number.isNaN(wantedAsUtc)) return '';

  // Initial guess: treat the wall-clock value as if it were already UTC.
  let candidateMs = wantedAsUtc;

  // At most a couple of iterations are ever needed: each correction is exact
  // once the candidate and the true instant fall on the same side of every
  // DST transition between them, which the first correction already
  // guarantees except in the rare case the correction itself crosses back
  // over a transition boundary.
  for (let i = 0; i < 3; i++) {
    const rendered = getClubWallClockParts(new Date(candidateMs));
    const renderedAsUtc = Date.UTC(
      rendered.year,
      rendered.month - 1,
      rendered.day,
      rendered.hour,
      rendered.minute,
    );
    const deltaMs = renderedAsUtc - wantedAsUtc;
    if (deltaMs === 0) break;
    candidateMs -= deltaMs;
  }

  return new Date(candidateMs).toISOString();
}

// ---------------------------------------------------------------------------
// Display formatting (#111)
//
// Every club date/time shown in the UI goes through these helpers, which
// always render in the club's zone (`Australia/Sydney`), never the viewer's.
// Without this, a phone set to `Australia/Brisbane` (no daylight saving)
// shows a 7:00am AEDT competition start as 6:00am. Tumbulgum sits on the
// QLD border, so that is a real share of members.
// ---------------------------------------------------------------------------

export type ClubDateInput = string | number | Date | null | undefined;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parses a display input to a Date, or null when absent/unparseable.
 * A bare calendar date (`YYYY-MM-DD`, e.g. a publish date or date of birth)
 * is pinned to 12:00 UTC, which falls on the same calendar day in Sydney
 * (22:00 AEST / 23:00 AEDT), so it never drifts a day either way.
 */
function toDate(value: ClubDateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const m = DATE_ONLY.exec(value.trim());
    if (m) return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function clubFormatter(options: Intl.DateTimeFormatOptions, locale: string): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { ...options, timeZone: CLUB_TIMEZONE });
    formatterCache.set(key, f);
  }
  return f;
}

/**
 * Formats an instant in club time with the given Intl options. Any
 * `timeZone` in `options` is overridden. Returns "" for empty/invalid input.
 */
export function formatClub(
  value: ClubDateInput,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-AU',
): string {
  const d = toDate(value);
  return d ? clubFormatter(options, locale).format(d) : '';
}

/** Club-time date. Default `26/09/2026`, matching `toLocaleDateString('en-AU')`. */
export function formatClubDate(
  value: ClubDateInput,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
): string {
  return formatClub(value, options);
}

/** Club-time clock time. Default `7:00 am`. */
export function formatClubTime(
  value: ClubDateInput,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true },
): string {
  return formatClub(value, options);
}

/** Club-time date and time. Default `26/09/2026, 10:41:00 am`, matching `toLocaleString('en-AU')`. */
export function formatClubDateTime(
  value: ClubDateInput,
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  },
): string {
  return formatClub(value, options);
}

/**
 * Compact club time for tight UI: `7am`, `7:30am`, `2pm`.
 */
export function formatClubTimeCompact(value: ClubDateInput): string {
  const d = toDate(value);
  if (!d) return '';
  const parts = clubFormatter({ hour: 'numeric', minute: '2-digit', hour12: true }, 'en-AU').formatToParts(d);
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const period = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toLowerCase().replace(/\./g, '');
  return `${hour}${minute === '00' ? '' : `:${minute}`}${period}`;
}

export interface ClubDateParts {
  /** `OCT` */
  monthShort: string;
  /** `18` */
  day: string;
  /** `Sun` */
  weekdayShort: string;
  /** `Sunday 18 October 2026`, for aria-labels. */
  fullDate: string;
}

/** Club-time calendar parts for a date block. Null for empty/invalid input. */
export function getClubDateParts(value: ClubDateInput): ClubDateParts | null {
  const d = toDate(value);
  if (!d) return null;
  const parts = clubFormatter({ weekday: 'short', day: 'numeric', month: 'short' }, 'en-AU').formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return {
    monthShort: get('month').replace('.', '').toUpperCase(),
    day: get('day'),
    weekdayShort: get('weekday').replace('.', ''),
    fullDate: formatClub(d, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      .replace(',', ''),
  };
}
